import {
  Box,
  Button,
  ButtonProps,
  Container,
  Flex,
  Heading,
  Image,
  Text,
} from '@everlywell/ui-kit';
import SectionContainer from 'components/PageSections/components/SectionContainer';
import { createSharedButtonProps } from 'components/PageSections/utils';
import RichTextWrapper from 'components/RichTextWrapper';
import { GatsbyImage } from 'gatsby-plugin-image';
import React from 'react';
import analytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import { ImageType } from 'utils/types';
import { CallToAction, ContentfulRichText } from 'utils/types/contentfulTypes';

// Up to 4 images one on each corner of the main image
const REST_OF_IMAGES_POSITIONS = [
  // Top right
  {
    top: [0],
    right: [0],
    marginTop: ['5%', null, '15%', '10%'],
    marginRight: ['2%', '10%'],
  },
  // Bottom left
  {
    bottom: [0],
    left: [0],
    marginBottom: ['5%', null, '15%', '10%'],
    marginLeft: ['2%', '10%'],
  },
  // Top right
  {
    bottom: [0],
    right: [0],
    marginBottom: ['5%', null, '15%', '10%'],
    marginRight: ['2%', '10%'],
  },
  // Bottom right
  {
    top: [0],
    left: [0],
    marginTop: ['5%', null, '15%', '10%'],
    marginLeft: ['2%', '10%'],
  },
];

export type BrandMomentBannerProps = {
  eyebrow?: string | null;
  eyebrowColor?: string | null;
  title?: string | null;
  description?: ContentfulRichText | null;
  mobileImages?: ImageType[] | null;
  desktopImages?: ImageType[] | null;
  backgroundColor?: string | null;
  cta: CallToAction | null;
};

/**
 * BrandMomentBanner component is used to display a brand moment banner with images and CTA
 * @param props BrandMomentBannerProps object containing eyebrow, title, description, mobileImages, desktopImages, backgroundColor, and cta
 * @returns JSX.Element containing brand moment banner
 */
const BrandMomentBanner = ({
  eyebrow,
  eyebrowColor,
  title,
  description,
  mobileImages,
  desktopImages,
  backgroundColor,
  cta,
}: BrandMomentBannerProps): JSX.Element => {
  const sharedButtonProps: ButtonProps = {
    ...createSharedButtonProps(cta?.url),
    variant: 'primary',
    colorScheme: 'whiteCS',
    id: `${cta?.label}-promo`,
    size: 'wide',
    onClick: () => trackClick?.(cta?.label),
  };

  const trackClick = (label?: string) => {
    analytics.track({
      event: ANALYTICS.EVENTS.CLICKED_BUTTON,
      data: {
        sectionHeader: title || 'No title provided',
        contentfulSection: 'BrandMomentBanner',
        ctaText: label,
      },
    });
  };

  // First image is the background image
  const mobileBackgroundImageUrl = mobileImages
    ? mobileImages[0].file?.url
    : undefined;
  const mainMobileImage = mobileImages ? mobileImages[1] : undefined;

  // Small images around the main image
  const restOfMobileImages = mobileImages ? mobileImages.slice(2) : [];

  // First image is the background image
  const desktopBackgroundImageUrl = desktopImages
    ? desktopImages[0].file?.url
    : undefined;
  const mainDesktopImage = desktopImages ? desktopImages[1] : undefined;

  // Small images around the main image
  const restOfDesktopImages = desktopImages ? desktopImages.slice(2) : [];

  return (
    <SectionContainer
      backgroundColor={backgroundColor || undefined}
      size="fluid"
    >
      <Container
        display="flex"
        flexDirection={['column', null, 'row']}
        size={['sm', null, 'xl']}
        marginX={['auto', 'auto']}
        paddingTop={[12, null, 0]}
        paddingBottom={[16, null, 0]}
        clipPath="ellipse(180% 60% at 50% 40%)"
        minHeight={['auto', null, '950px']}
        gap={[null, null, 5]}
      >
        <Flex
          flex={[null, null, 1]}
          flexDirection="column"
          alignItems={['center', null, 'flex-start']}
          justifyContent="center"
        >
          {eyebrow ? (
            <Text
              marginX="auto"
              size="lg"
              color={eyebrowColor || 'sky.base'}
              textAlign={['center', null, 'left']}
              marginBottom={[3, null, 4]}
              width="100%"
            >
              {eyebrow}
            </Text>
          ) : null}

          {title ? (
            <Heading
              as="h2"
              size="2xl"
              textAlign={['center', null, 'left']}
              marginBottom={[6, null, 10]}
              color="tints.white"
            >
              {title}
            </Heading>
          ) : null}

          {/* For desktop */}
          {description ? (
            <Box
              display={['none', null, 'block']}
              maxWidth="500px"
              sx={{
                '*': {
                  color: 'tints.white',
                  textAlign: ['center', null, 'left'],
                  fontSize: 'lg',
                  marginBottom: [5, 10],
                },
              }}
            >
              <RichTextWrapper
                contentfulRichText={description}
              ></RichTextWrapper>
            </Box>
          ) : null}

          {/* Desktop CTA */}
          {cta ? (
            <Button
              display={['none', null, 'block']}
              {...sharedButtonProps}
              width="fit-content"
            >
              {cta.label}
            </Button>
          ) : null}
        </Flex>

        {/* Images Container */}
        <Flex
          flexDirection="column"
          justifyContent="center"
          position="relative"
          overflow="hidden"
          marginTop={[8, null, 0]}
          marginBottom={[8, null, 0]}
          flex={[null, null, 2]}
        >
          {/* Rotating background image mobile and desktop */}
          <Box
            backgroundImage={[
              `url(${mobileBackgroundImageUrl})`,
              null,
              `url(${desktopBackgroundImageUrl})`,
            ]}
            backgroundSize="contain"
            backgroundRepeat="no-repeat"
            backgroundPosition="center"
            width={['100%']}
            height={['100%']}
            position="absolute"
            animation="rotate 80s linear infinite"
            sx={{
              '@keyframes rotate': {
                '0%': {
                  transform: 'rotate(0deg)',
                },
                '100%': {
                  transform: 'rotate(360deg)',
                },
              },
            }}
          />

          {/* Mobile images */}
          <Box display={['flex', null, 'none']} padding="10">
            {mainMobileImage ? (
              <Image
                as={GatsbyImage}
                image={mainMobileImage?.gatsbyImageData}
                margin="0 auto"
                alt={mainMobileImage?.description || ''}
              />
            ) : null}

            {restOfMobileImages.length
              ? restOfMobileImages.map((image, index) => (
                  <Image
                    key={index}
                    as={GatsbyImage}
                    image={image?.gatsbyImageData}
                    alt={image?.description || ''}
                    position="absolute"
                    width={['35%', '30%']}
                    {...REST_OF_IMAGES_POSITIONS[index]}
                  />
                ))
              : null}
          </Box>

          {/* Desktop images */}
          <Box display={['none', null, 'flex']}>
            {mainDesktopImage ? (
              <Image
                as={GatsbyImage}
                image={mainDesktopImage?.gatsbyImageData}
                margin="0 auto"
                alt={mainDesktopImage?.description || ''}
              />
            ) : null}

            {restOfDesktopImages.length
              ? restOfDesktopImages.map((image, index) => (
                  <Image
                    key={index}
                    as={GatsbyImage}
                    image={image?.gatsbyImageData}
                    alt={image?.description || ''}
                    position="absolute"
                    width={['35%', '30%']}
                    {...REST_OF_IMAGES_POSITIONS[index]}
                  />
                ))
              : null}
          </Box>
        </Flex>

        {/* For mobile */}
        {description ? (
          <Box
            display={['block', null, 'none']}
            marginBottom="5"
            marginX="auto"
            maxWidth="700px"
            paddingX="4"
            sx={{
              '*': {
                color: 'tints.white',
                textAlign: 'center',
                fontSize: 'md',
              },
            }}
          >
            <RichTextWrapper contentfulRichText={description}></RichTextWrapper>
          </Box>
        ) : null}

        {/* Mobile CTA */}
        {cta ? (
          <Button display={['flex', null, 'none']} {...sharedButtonProps}>
            {cta.label}
          </Button>
        ) : null}
      </Container>
    </SectionContainer>
  );
};

export default BrandMomentBanner;
